import _merge from "lodash/merge";
import { getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import { getDimensions } from '@kentik/ui-shared/query/query';
import { formatRowName, getBaseChartStyle } from 'app/views/ude/dataviews/util/highcharts.util';
import { generateTimelineOptions } from 'app/views/ude/dataviews/util/timelineView';
import { generateSankeyOptions } from 'app/views/ude/dataviews/util/sankeyView';
import { dataViewTypes } from 'app/stores/$dataviews';
import { getTimezone, getTimezoneShortName } from 'core/util/timezone';
import { getMirroredItemName } from '@kentik/ui-shared/query/mirror';
import { formatMetricValue, getDatasetPrefix, getDnsCodeLabel, getHealthLabel, getHttpStatusCodeLabel, parseUnit } from './unitFormatter';

/**
 * Formatter function for dimension values.
 * Used to convert raw dimension values (like agent IDs) to display names.
 */

/**
 * Y-axis configuration for health metrics
 * Shows only the 3 valid health status labels: Critical (100), Warning (200), Healthy (300)
 */
const HEALTH_YAXIS_CONFIG = {
  tickPositions: [100, 200, 300],
  min: 50,
  max: 350,
  labels: {
    formatter() {
      return getHealthLabel(this.value);
    }
  }
};

/**
 * Y-axis configuration for DNS response code metrics
 * Shows common DNS response codes (0-5) with their labels
 */
const DNS_CODE_YAXIS_CONFIG = {
  tickPositions: [0, 1, 2, 3, 4, 5],
  min: -0.5,
  max: 5.5,
  labels: {
    formatter() {
      return getDnsCodeLabel(this.value);
    }
  }
};

/**
 * Y-axis configuration for HTTP status code metrics
 * Shows major status code categories to avoid crowding
 */
const HTTP_STATUS_CODE_YAXIS_CONFIG = {
  // Show just the major category representatives: 2xx, 3xx, 4xx, 5xx
  tickPositions: [200, 300, 400, 500],
  min: 150,
  max: 550,
  labels: {
    formatter() {
      return getHttpStatusCodeLabel(this.value);
    }
  }
};
const SupportedVizTypes = ['bar', 'line', 'matrix', 'pie', 'sankey', 'stackedArea', 'stackedAreaHundredPercent', 'stackedBar', 'sunburst', 'timelineView'];
export const HighchartsVizTypes = SupportedVizTypes;
export const TimeSeriesVizTypes = ['line', 'stackedBar', 'stackedArea', 'stackedAreaHundredPercent'];
const AggregateSeriesVizTypes = ['bar', 'pie'];
const VizToChartTypes = {
  bar: 'bar',
  line: 'line',
  matrix: 'heatmap',
  pie: 'pie',
  sankey: 'sankey',
  stackedArea: 'area',
  stackedAreaHundredPercent: 'area',
  stackedBar: 'column',
  sunburst: 'sunburst',
  timelineView: 'xrange'
};
function groupPointsByRow(points) {
  const groups = new Map();
  for (const ctx of points) {
    const rowName = ctx.series.name;
    const metricLabel = ctx.series.yAxis.userOptions.title?.text || '';
    const yAxis = ctx.series.yAxis.options.opposite ? 'mirror' : 'original';
    if (!groups.has(rowName)) {
      groups.set(rowName, []);
    }
    groups.get(rowName)?.push({
      point: ctx.point,
      color: ctx.color,
      metricLabel,
      yAxis
    });
  }
  return groups;
}
function generateTimeSeriesOptions(queryModel, measurement, theme, metricName, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const vizType = queryModel.settings.chart.type;
  const chartType = VizToChartTypes[vizType];
  const timezone = getTimezone(queryModel.settings.chart.xAxis?.timezone || 'UTC');
  const unit = measurementMetric?.Unit;
  const unitFormat = parseUnit(unit);
  const isHealthMetric = unitFormat.type === 'health';
  const isDnsCodeMetric = unitFormat.type === 'dns_code';
  const isHttpStatusCodeMetric = unitFormat.type === 'http_status_code';
  const isMetricsMeasurement = measurement.measurement.startsWith('/metrics');
  const dimensions = getDimensions(queryModel.query);
  let stacking;
  if (vizType === 'stackedArea' || vizType === 'stackedBar') {
    stacking = 'normal';
  } else if (vizType === 'stackedAreaHundredPercent') {
    stacking = 'percent';
  }
  const getMax = (rows, maxMetric) => {
    const data = rows.map(row => Math.max(...row.values[maxMetric].values.filter(v => v !== null)));
    if (stacking === 'percent') {
      return 100;
    }
    if (stacking === 'normal') {
      return data.reduce((sum, val) => sum + val, 0);
    }
    return Math.max(0, ...data);
  };

  // Get all data for determining consistent prefix/suffix
  const {
    mainQueryRequest,
    mirrorQueryRequest
  } = queryModel;
  const mirroredMetric = queryModel.settings.chart.yAxis?.mirrorMode ? getMirroredItemName(measurement, 'metric', metricName) || metricName : null;
  const rows = mainQueryRequest.results.rows.filter(row => row.values[metricName]).slice(0, queryModel.effectiveVizLimit);
  const seriesRowIds = {
    [mainQueryRequest.type]: rows.map(row => row.id)
  };
  const allData = rows.flatMap(row => row.values[metricName]?.values || []).filter(v => v != null);
  const prefix = getDatasetPrefix(allData, unit);
  const max = getMax(rows, metricName);

  // Build Y-axis config - use special config for health, dns_code, and http_status_code metrics
  const yAxisTitle = measurementMetric?.Label || metricName;
  const yAxisConfig = {
    crosshair: false,
    min: 0,
    max: undefined,
    reversedStacks: false,
    title: {
      text: yAxisTitle
    }
  };
  const yAxis = [yAxisConfig];
  if (isHealthMetric) {
    Object.assign(yAxisConfig, HEALTH_YAXIS_CONFIG);
  } else if (isDnsCodeMetric) {
    Object.assign(yAxisConfig, DNS_CODE_YAXIS_CONFIG);
  } else if (isHttpStatusCodeMetric) {
    Object.assign(yAxisConfig, HTTP_STATUS_CODE_YAXIS_CONFIG);
  } else {
    Object.assign(yAxisConfig, {
      type: queryModel.settings.chart?.yAxis?.type,
      labels: {
        formatter() {
          const formatted = formatMetricValue(Math.abs(this.value), unit, {
            forcePrefix: prefix
          });
          return formatted.displayFull;
        }
      }
    });
  }
  const formatSeries = (row, seriesMetricName, yAxisIdx) => {
    const {
      timestamps
    } = row;
    return {
      id: `${row.id}${yAxisIdx > 0 ? `-${yAxisIdx}` : ''}`,
      name: formatRowName(row.get(), dimensions, dimensionFormatter),
      type: chartType,
      yAxis: yAxisIdx,
      color: queryModel.getSeriesColor(row.id),
      visible: queryModel.isSeriesVisible(row.id),
      linkedTo: yAxisIdx > 0 ? row.id : undefined,
      data: row.values[seriesMetricName].values.map((value, idx) => [timestamps[idx] * 1000, value && yAxisIdx > 0 ? value * -1 : value, formatMetricValue(isMetricsMeasurement ? value : value ?? 0, unit, {
        values: measurementMetric?.Values,
        forcePrefix: prefix
      }).displayFull, getTimezoneShortName(timezone, timestamps[idx] * 1000)])
    };
  };
  const series = rows.map(row => formatSeries(row, metricName, 0));
  if (mirroredMetric && mirrorQueryRequest.hasRun) {
    const mirrorMeasurementMetric = getMeasurementMetric(measurement, mirroredMetric);
    const mirrorRows = mirrorQueryRequest.results.rows.filter(row => row.values[mirroredMetric]).slice(0, queryModel.effectiveVizLimit);
    const mirrorMax = getMax(mirrorRows, mirroredMetric);
    seriesRowIds[mirrorQueryRequest.type] = mirrorRows.map(row => row.id);
    const originalFormatter = yAxisConfig.labels?.formatter;
    yAxis[0].min = -max;
    yAxis[0].max = max;
    yAxis[0].labels = {
      formatter() {
        const value = this.value;
        if (value < 0) {
          return '';
        }
        return originalFormatter ? originalFormatter.call(this, this) : `${value}`;
      }
    };
    yAxis.push({
      ...yAxisConfig,
      linkedTo: queryModel.settings.chart.yAxis?.syncAxes ? 0 : undefined,
      opposite: true,
      min: -mirrorMax,
      max: mirrorMax,
      plotLines: [{
        value: 0,
        color: theme.colors.body,
        width: 2,
        zIndex: 5
      }],
      title: {
        text: mirrorMeasurementMetric?.Label || mirroredMetric
      },
      labels: {
        formatter() {
          const value = this.value;
          if (value > 0) {
            return '';
          }
          return originalFormatter ? originalFormatter.call(this, this) : `${value}`;
        }
      }
    });
    series.push(...mirrorRows.map(row => formatSeries(row, mirroredMetric, 1)));
  }
  const options = {
    chart: {
      type: chartType
    },
    colors: queryModel.colors,
    legend: {
      enabled: false
    },
    xAxis: {
      type: 'datetime',
      crosshair: true
    },
    yAxis,
    time: {
      timezone
    },
    tooltip: {
      shared: true,
      followPointer: true,
      useHTML: true,
      positioner(labelWidth, _labelHeight, point) {
        const OFFSET_X = 20;
        const OFFSET_Y = 24;
        const c = this.chart;
        const plotX = point.plotX + c.plotLeft;
        const plotY = point.plotY + c.plotTop;
        let x = plotX + OFFSET_X + labelWidth > c.chartWidth ? plotX - labelWidth - OFFSET_X : plotX + OFFSET_X;
        x = Math.max(0, Math.min(x, c.chartWidth - labelWidth));
        const y = plotY + OFFSET_Y;
        return {
          x,
          y
        };
      },
      formatter() {
        const points = this.points || [];
        const groups = groupPointsByRow(points);
        const {
          timezone: timezoneDisplay
        } = points[0]?.point.options.custom || {};
        let columns = [{
          label: 'Value',
          yAxis: 'original'
        }];
        if (mirroredMetric && mirrorQueryRequest.hasRun) {
          columns = [{
            label: 'Original',
            yAxis: 'original'
          }, {
            label: 'Mirror',
            yAxis: 'mirror'
          }];
        }
        return `
          <div>
            <strong>${this.series.chart.time.dateFormat('%Y-%m-%d %H:%M', this.x)} ${timezoneDisplay}</strong>
            <table>
              ${columns.length > 1 ? `
                    <thead>
                      <tr>
                        <th></th>
                        ${columns.map(col => `
                              <th style="text-align: left">${col.label}</th>
                              ${stacking === 'percent' ? '<th></th>' : ''}
                            `).join('')}
                      </tr>
                    </thead>
                  ` : ''}
              <tbody>
                ${Array.from(groups.entries()).map(([rowName, groupPoints]) => {
          const {
            color
          } = groupPoints[0];
          return `
                      <tr>
                        <td><span style="color:${color}">\u25CF</span> ${rowName}</td>
                        ${columns.map(col => {
            const point = groupPoints.find(p => p.yAxis === col.yAxis)?.point;
            const {
              percentage
            } = point || {};
            return `
                              <td><b>${point?.options.custom?.formatted || ''}</b></td>
                              ${stacking === 'percent' ? `<td>${percentage !== undefined ? `(${Math.abs(percentage).toFixed(1)}%)` : ''}</td>` : ''}
                            `;
          }).join('')}
                      </tr>
                    `;
        }).join('')}
              </tbody>
            </table>
          </div>
        `;
      }
    },
    plotOptions: {
      series: {
        keys: ['x', 'y', 'custom.formatted', 'custom.timezone'],
        stacking,
        ...(isMetricsMeasurement && {
          connectNulls: true
        })
      }
    },
    series
  };
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds
  };
}
function generateAggregateSeriesOptions(queryModel, theme, measurement, metricName, rollupName, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const visualizationRows = queryModel.mainQueryRequest.results.rows.slice(0, queryModel.effectiveVizLimit);
  const chartType = VizToChartTypes[queryModel.settings.chart.type];
  const unit = measurementMetric?.Unit;
  const dimensions = getDimensions(queryModel.query);

  // Get all data for determining consistent prefix/suffix
  const allData = queryModel.mainQueryRequest.results.rows.flatMap(row => row.rollups[rollupName]).filter(v => v != null);
  const prefix = getDatasetPrefix(allData, unit);
  const options = {
    chart: {
      type: chartType
    },
    colors: queryModel.colors,
    legend: {
      enabled: false
    },
    xAxis: {
      type: 'category'
    },
    yAxis: {
      type: queryModel.settings.chart.yAxis?.type,
      title: {
        text: measurementMetric?.Label || metricName
      },
      labels: {
        formatter() {
          const formatted = formatMetricValue(this.value, unit, {
            forcePrefix: prefix
          });
          return formatted.displayFull;
        }
      }
    },
    tooltip: {
      shared: true,
      followPointer: true,
      useHTML: true,
      valueDecimals: 1,
      headerFormat: '<div>{point.key}</div><table>',
      pointFormatter() {
        return `
          <tr>
            <td><span style="color:${this.color}">\u25CF</span></td>
            <td><b>${this.options.custom?.formatted}</b></td>
            ${this.percentage !== undefined ? `<td>(${this.percentage.toFixed(1)}%)</td>` : ''}
          </tr>`;
      },
      footerFormat: '</table>'
    },
    series: [{
      name: measurementMetric?.Label || metricName,
      type: chartType,
      data: visualizationRows.map(row => {
        const value = row.rollups[rollupName];
        return {
          id: row.id,
          name: formatRowName(row.get(), dimensions, dimensionFormatter),
          y: value,
          custom: {
            formatted: formatMetricValue(value, unit, {
              forcePrefix: prefix
            }).displayFull
          }
        };
      })
    }]
  };
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: {
      [queryModel.mainQueryRequest.type]: visualizationRows.map(r => r.id)
    }
  };
}
function generateSunburstOptions(queryModel, theme, metricName, rollupName, measurement, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const limit = queryModel.effectiveVizLimit;
  const visualizationRows = queryModel.mainQueryRequest.results.rows.slice(0, limit);
  const unit = measurementMetric?.Unit;

  // Single dataset-wide prefix so every arc's tooltip shows a consistent unit.
  const allData = visualizationRows.map(row => row.rollups[rollupName]).filter(v => v != null);
  const prefix = getDatasetPrefix(allData, unit);
  const dimensions = getDimensions(queryModel.query);
  const data = [{
    id: 'root',
    parent: '',
    name: `Total of Top ${limit}`,
    color: 'transparent'
  }];
  visualizationRows.forEach(row => {
    const path = dimensions.flatMap(dimension => {
      const rawName = row.dimensions[dimension.name];
      const formattedName = dimensionFormatter ? dimensionFormatter(dimension.name, rawName) : rawName;
      return dimension.name.includes('bgp_aspath') ? formattedName.split(' ') : formattedName;
    });
    let parent = 'root';
    path.forEach((name, idx) => {
      const id = `${parent}-${name}`;
      let node = data.find(n => n.id === id);
      if (!node) {
        node = {
          id,
          parent,
          name
        };
        if (parent === 'root') {
          node.color = queryModel.getSeriesColor(row.id);
        }
        data.push(node);
      }
      if (idx === path.length - 1) {
        node.value ??= 0;
        node.value += row.rollups[rollupName];
      }
      parent = id;
    });
  });
  const options = {
    chart: {
      type: 'sunburst'
    },
    colors: queryModel.colors,
    legend: {
      enabled: false
    },
    tooltip: {
      shared: false,
      followPointer: true,
      useHTML: true,
      valueDecimals: 1,
      headerFormat: '<div>{point.key}</div><table>',
      pointFormatter() {
        const value = Number(this.value) || 0;
        const formatted = formatMetricValue(value, unit, {
          forcePrefix: prefix
        }).displayFull;
        return `
          <tr>
            <td><span style="color:${this.color}">\u25CF</span></td>
            <td><b>${formatted}</b></td>
          </tr>`;
      },
      footerFormat: '</table>'
    },
    plotOptions: {
      sunburst: {
        dataLabels: {
          rotationMode: 'circular',
          style: {
            textOutline: 'none'
          },
          filter: {
            property: 'outerArcLength',
            operator: '>',
            value: 64
          }
        }
      }
    },
    series: [{
      name: measurementMetric?.Label || metricName,
      type: 'sunburst',
      cursor: 'pointer',
      allowTraversingTree: true,
      levels: [{
        level: 1,
        dataLabels: {
          enabled: false
        }
      }, {
        level: 2,
        colorByPoint: true
      }, {
        level: 3,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 4,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 5,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 6,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 7,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 8,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 9,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }],
      data
    }]
  };
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: {
      [queryModel.mainQueryRequest.type]: visualizationRows.map(r => r.id)
    }
  };
}

/**
 * Apply threshold configuration to Highcharts options.
 * Adds plotLines and/or plotBands to the yAxis based on threshold settings.
 *
 * For percentage mode, threshold values represent percentages of the data range (0-100).
 * For absolute mode, threshold values are used as-is.
 */
function applyThresholds(options, thresholds, queryModel) {
  const {
    steps,
    mode,
    display,
    colorSeries
  } = thresholds;
  if (display === 'off' || !steps || steps.length < 2) {
    return;
  }

  // Sort steps ascending by value (base step with null goes first)
  const sortedSteps = [...steps].sort((a, b) => {
    if (a.value === null) {
      return -1;
    }
    if (b.value === null) {
      return 1;
    }
    return a.value - b.value;
  });

  // For percentage mode, compute data range min/max from results
  let dataMin = 0;
  let dataMax = 100;
  if (mode === 'percentage') {
    const allValues = queryModel.mainQueryRequest.results.rows.flatMap(row => Object.values(row.values).flatMap(metric => metric?.values?.filter(v => v != null) || []));
    if (allValues.length > 0) {
      dataMin = Math.min(...allValues);
      dataMax = Math.max(...allValues);
    }
  }
  const resolveValue = step => {
    if (step.value === null) {
      return dataMin;
    }
    if (mode === 'percentage') {
      return dataMin + step.value / 100 * (dataMax - dataMin);
    }
    return step.value;
  };
  const showLines = display === 'line' || display === 'dashed-line' || display === 'line+area' || display === 'dashed-line+area';
  const showAreas = display === 'area' || display === 'line+area' || display === 'dashed-line+area';
  const dashed = display === 'dashed-line' || display === 'dashed-line+area';
  const plotLines = [];
  const plotBands = [];

  // Non-base steps get plotLines
  if (showLines) {
    for (const step of sortedSteps) {
      if (step.value !== null) {
        plotLines.push({
          value: resolveValue(step),
          color: step.color,
          width: 2,
          dashStyle: dashed ? 'Dash' : 'Solid',
          zIndex: 5,
          label: {
            text: `${step.value}${mode === 'percentage' ? '%' : ''}`,
            style: {
              color: step.color,
              fontSize: '11px'
            }
          }
        });
      }
    }
  }

  // PlotBands: fill regions between steps
  if (showAreas) {
    for (let i = 0; i < sortedSteps.length; i++) {
      const step = sortedSteps[i];
      const from = resolveValue(step);
      const to = i < sortedSteps.length - 1 ? resolveValue(sortedSteps[i + 1]) : Number.MAX_VALUE;
      plotBands.push({
        from,
        to,
        color: `${step.color}30`,
        // ~19% opacity hex suffix
        zIndex: 0
      });
    }
  }

  // Always explicitly set plotLines and plotBands (even if empty) so that
  // chart.update() clears stale decorations when switching display modes.
  const yAxis = options.yAxis;
  if (yAxis) {
    yAxis.plotLines = plotLines;
    yAxis.plotBands = plotBands;
  }

  // Apply or clear color zones on each series.
  if (options.series) {
    if (colorSeries && showAreas) {
      const zones = sortedSteps.map((step, i) => {
        const zone = {
          color: step.color,
          fillColor: `${step.color}40`
        };
        if (i < sortedSteps.length - 1) {
          zone.value = resolveValue(sortedSteps[i + 1]);
        }
        return zone;
      });
      for (const series of options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = 'y';
        s.zones = zones;
      }
    } else {
      // Clear zones so series revert to original colors
      for (const series of options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = undefined;
        s.zones = [];
      }
    }
  }
}
export function generateHighchartsOptions(queryModel, measurement, theme, dimensionFormatter) {
  const vizType = queryModel.settings.chart.type;
  const metricName = queryModel.primaryMetric;
  const dataView = dataViewTypes?.[vizType || 'line'];
  const requirements = dataView?.requirements?.(queryModel.query);
  if (requirements) {
    console.info(`User attempted an incompatible query: ${queryModel.query} for ${vizType}`);
    const requirementsText = requirements.description ? `${requirements.title} ${requirements.description}` : requirements.title;
    return {
      options: {
        title: {
          text: requirementsText
        }
      },
      seriesRowIds: {}
    };
  }
  let result;
  if (vizType && TimeSeriesVizTypes.includes(vizType)) {
    result = generateTimeSeriesOptions(queryModel, measurement, theme, metricName, dimensionFormatter);
  } else if (vizType && AggregateSeriesVizTypes.includes(vizType)) {
    const rollupKey = queryModel.primaryRollupKey;
    result = generateAggregateSeriesOptions(queryModel, theme, measurement, metricName, rollupKey, dimensionFormatter);
  } else if (vizType === 'sunburst') {
    const rollupKey = queryModel.primaryRollupKey;
    result = generateSunburstOptions(queryModel, theme, metricName, rollupKey, measurement, dimensionFormatter);
  } else if (vizType === 'sankey') {
    const rollupKey = queryModel.primaryRollupKey;
    // Returns early: sankey has no yAxis or zoneable series for thresholds to decorate.
    return generateSankeyOptions(queryModel, theme, metricName, rollupKey, measurement, dimensionFormatter);
  } else if (vizType === 'timelineView') {
    result = generateTimelineOptions({
      queryModel,
      theme,
      measurement,
      metricName,
      dimensionFormatter
    });
  } else {
    // TODO matrix
    console.error(`Unsupported VizType: ${vizType}`);
    return {
      options: {
        title: {
          text: 'Coming Soon!'
        }
      },
      seriesRowIds: {}
    };
  }

  // Apply thresholds to all chart types that have a yAxis
  const {
    thresholds
  } = queryModel.settings.chart;
  if (thresholds?.enabled && thresholds.display !== 'off') {
    applyThresholds(result.options, thresholds, queryModel);
  } else {
    // Explicitly clear threshold decorations so chart.update() removes stale ones
    const yAxis = result.options.yAxis;
    if (yAxis) {
      yAxis.plotLines = [];
      yAxis.plotBands = [];
    }
    if (result.options.series) {
      for (const series of result.options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = undefined;
        s.zones = [];
      }
    }
  }
  return result;
}